import { useEffect, useState, useCallback } from "react";
import {
  PhoneCall, Plus, Pencil, Trash2, X, Check, Loader2, CalendarClock, User,
} from "lucide-react";
import api from "../../api/axios";

const todayStr = () => new Date().toISOString().split("T")[0];

export default function FollowUps() {
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [banner, setBanner] = useState(null);

  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ customer_id: "", note: "", next_follow_date: todayStr(), status: "Pending" });
  const [saving, setSaving] = useState(false);

  const flash = (type, text) => { setBanner({ type, text }); setTimeout(() => setBanner(null), 3000); };

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (statusFilter) params.status = statusFilter;
    const res = await api.get("/api/followups", { params });
    setItems(res.data.followups || []);
    setLoading(false);
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/api/customers").then((r) => setCustomers(r.data.customers || [])); }, []);

  const openAdd = () => { setEditId(null); setForm({ customer_id: "", note: "", next_follow_date: todayStr(), status: "Pending" }); setShowForm(true); };
  const openEdit = (f) => { setEditId(f.id); setForm({ customer_id: f.customer_id, note: f.note || "", next_follow_date: f.next_follow_date || todayStr(), status: f.status || "Pending" }); setShowForm(true); };

  const save = async () => {
    if (!editId && !form.customer_id) { flash("error", "Please select a customer."); return; }
    setSaving(true);
    try {
      if (editId) await api.put(`/api/followups/${editId}`, { note: form.note, next_follow_date: form.next_follow_date, status: form.status });
      else await api.post("/api/followups", form);
      setShowForm(false); flash("success", editId ? "Follow-up updated." : "Follow-up added."); load();
    } catch (err) { flash("error", err.response?.data?.message || "Failed to save."); }
    finally { setSaving(false); }
  };

  const del = async (id) => { if (!window.confirm("Delete this follow-up?")) return; await api.delete(`/api/followups/${id}`); flash("success", "Deleted."); load(); };
  const toggleStatus = async (f) => { await api.put(`/api/followups/${f.id}`, { status: f.status === "Done" ? "Pending" : "Done" }); load(); };

  const isOverdue = (d, status) => d && status !== "Done" && d < todayStr();

  return (
    <div className="mx-auto max-w-6xl" data-testid="followups-page">
      {banner && (
        <div className={`mb-4 rounded-lg px-4 py-2.5 text-sm font-medium ${banner.type === "success" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-red-50 text-red-700 border border-red-200"}`}>
          {banner.text}
        </div>
      )}

      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-indigo-600 to-blue-500 grid place-items-center text-white shadow-md">
            <PhoneCall size={20} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Follow Ups</h1>
            <p className="text-sm text-slate-500">Track next follow-up dates & notes per customer</p>
          </div>
        </div>
        <div className="flex gap-2">
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="followup-filter-status" className="h-10 rounded-lg border border-slate-300 bg-white px-2 text-sm">
            <option value="">All</option>
            <option value="Pending">Pending</option>
            <option value="Done">Done</option>
          </select>
          <button onClick={openAdd} data-testid="followup-add-btn" className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3.5 py-2 text-sm font-semibold text-white hover:bg-indigo-700"><Plus size={16} /> Add Follow-up</button>
        </div>
      </div>

      {loading ? (
        <div className="py-16 text-center text-slate-400"><Loader2 className="mx-auto animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-white py-16 text-center text-slate-400">No follow-ups yet.</div>
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {items.map((f) => (
            <div key={f.id} data-testid={`followup-card-${f.id}`} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  <div className="grid h-9 w-9 place-items-center rounded-lg bg-indigo-100 text-indigo-700"><User size={16} /></div>
                  <div>
                    <p className="font-semibold text-slate-900">{f.customer_name}</p>
                    <p className="text-xs text-slate-500">{f.customer_phone || "-"}</p>
                  </div>
                </div>
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${f.status === "Done" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{f.status}</span>
              </div>
              {f.note && <p className="mt-3 rounded-lg bg-slate-50 p-2.5 text-sm text-slate-700 whitespace-pre-line">{f.note}</p>}
              <div className="mt-3 flex items-center justify-between">
                <span className={`inline-flex items-center gap-1.5 text-sm ${isOverdue(f.next_follow_date, f.status) ? "text-red-600 font-medium" : "text-slate-600"}`}>
                  <CalendarClock size={15} /> {f.next_follow_date || "No date"}{isOverdue(f.next_follow_date, f.status) ? " · Overdue" : ""}
                </span>
                <div className="flex gap-1.5">
                  <button onClick={() => toggleStatus(f)} data-testid={`followup-toggle-${f.id}`} className="rounded-md p-1.5 text-slate-500 hover:bg-emerald-50 hover:text-emerald-600" title="Toggle done"><Check size={15} /></button>
                  <button onClick={() => openEdit(f)} data-testid={`followup-edit-${f.id}`} className="rounded-md p-1.5 text-slate-500 hover:bg-blue-50 hover:text-blue-600"><Pencil size={15} /></button>
                  <button onClick={() => del(f.id)} data-testid={`followup-del-${f.id}`} className="rounded-md p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-600"><Trash2 size={15} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/50 p-4 backdrop-blur-sm">
          <div className="my-8 w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-bold text-slate-900">{editId ? "Edit Follow-up" : "Add Follow-up"}</h3>
              <button onClick={() => setShowForm(false)} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100"><X size={20} /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">Customer</label>
                <select value={form.customer_id} onChange={(e) => setForm({ ...form, customer_id: e.target.value })} disabled={!!editId} data-testid="followup-form-customer" className="h-10 w-full rounded-md border border-slate-300 bg-white px-2 text-sm disabled:bg-slate-100">
                  <option value="">Select customer</option>
                  {customers.map((c) => <option key={c.id} value={c.id}>{c.customer_name} · {c.phone}</option>)}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">Next Follow-up Date</label>
                <input type="date" value={form.next_follow_date} onChange={(e) => setForm({ ...form, next_follow_date: e.target.value })} data-testid="followup-form-date" className="h-10 w-full rounded-md border border-slate-300 px-2 text-sm" />
              </div>
              <div>
                <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">Notes</label>
                <textarea rows={4} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="What to follow up about…" data-testid="followup-form-note" className="w-full resize-none rounded-md border border-slate-300 p-2.5 text-sm" />
              </div>
              <div>
                <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-slate-500">Status</label>
                <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="followup-form-status" className="h-10 w-full rounded-md border border-slate-300 bg-white px-2 text-sm">
                  <option value="Pending">Pending</option>
                  <option value="Done">Done</option>
                </select>
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setShowForm(false)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600">Cancel</button>
              <button onClick={save} disabled={saving} data-testid="followup-form-save" className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-60">
                {saving ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
